/**
 * Hook to manage conversation state based on URL routing.
 * URL structures:
 * - /:orgSlug/chat - new chat with default agency
 * - /:orgSlug/chat/:agencySlug - new chat with specific agency
 * - /:orgSlug/chat/:agencySlug/:conversationId - existing conversation with agency
 * - /:orgSlug/c/:conversationId - existing conversation without agency (legacy/default)
 */

import type { ConversationResponse } from '@saaga/shared/interfaces/conversation.interface'
import { useGetAgentConversationQuery } from '@saaga/shared/store/api.store'
import {
  clearConversationStreaming,
  setActiveConversation,
} from '@saaga/shared/store/slices/chat.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { orgRoutes } from '@/routes/org-routes.const'

type ChatRouteParams = {
  agencySlug?: string
  conversationId?: string
} & Record<string, string | undefined>

interface UseChatConversationReturn {
  conversationId: string | null
  agencySlug: string | null
  conversation: ConversationResponse | undefined
  isLoading: boolean
  isNewConversation: boolean
  navigateToConversation: (newConversationId: string, newAgencySlug?: string) => void
}

export const useChatConversation = (): UseChatConversationReturn => {
  const dispatch = useAppDispatch()
  const { agencySlug: agencySlugParam, conversationId: conversationIdParam } =
    useParams<ChatRouteParams>()
  const navigate = useNavigate()
  const location = useLocation()
  const orgSlug = useOrgSlug()

  const isLegacyConversationRoute = location.pathname.includes(`/${orgRoutes.conversation}/`)
  const conversationId = conversationIdParam ?? null
  const agencySlug = isLegacyConversationRoute ? null : (agencySlugParam ?? null)
  const isNewConversation = !conversationId

  const { data: fetchedConversation, isLoading } = useGetAgentConversationQuery(
    conversationId ?? '',
    { skip: !conversationId }
  )

  // When skipped, RTK Query keeps stale data - explicitly return undefined for new conversations
  const conversation = isNewConversation ? undefined : fetchedConversation

  const prevConversationIdRef = useRef<string | null>(null)

  useEffect(() => {
    const prevId = prevConversationIdRef.current
    const shouldClearStreaming = prevId !== null || conversationId === null

    if (shouldClearStreaming && prevId) {
      dispatch(clearConversationStreaming({ conversationId: prevId }))
    }
    dispatch(setActiveConversation(conversationId ? { id: conversationId } : null))
    prevConversationIdRef.current = conversationId
  }, [dispatch, conversationId])

  const navigateToConversation = useCallback(
    (newConversationId: string, newAgencySlug?: string) => {
      if (!orgSlug) return
      const slug = newAgencySlug ?? agencySlug
      if (slug) {
        navigate(`/${orgSlug}/${orgRoutes.chat}/${slug}/${newConversationId}`, { replace: true })
        return
      }
      navigate(`/${orgSlug}/${orgRoutes.conversation}/${newConversationId}`, { replace: true })
    },
    [navigate, orgSlug, agencySlug]
  )

  return useMemo(
    () => ({
      conversationId,
      agencySlug,
      conversation,
      isLoading,
      isNewConversation,
      navigateToConversation,
    }),
    [conversationId, agencySlug, conversation, isLoading, isNewConversation, navigateToConversation]
  )
}
